import { Layers2 } from "lucide-react";
import { Link } from "react-router";

import { Badge, Progress, cn, focusRingClassName } from "@ceedero/ui";

import { useTranslation } from "@/localization/hooks/use-translation";
import { formatByteSize } from "@/music-catalog/utils/catalog-view";
import { ROUTES } from "@/navigation/constants/routes";
import { useDisplaySettingsStore } from "@/settings/store/use-display-settings-store";
import { formatAlbumLength } from "@/settings/utils/format-album-length";

import type { ReactNode } from "react";

/**
 * One figure, set at poster size, with the whole cell as the way to the page
 * that figure is about. The link is the label, so what a screen reader announces
 * is "Projects" rather than "20".
 */
const LedgerFigure = ({
  children,
  label,
  note,
  to,
}: {
  children: ReactNode;
  label: string;
  note?: string | undefined;
  to: string;
}) => (
  <div className="group relative flex min-w-0 flex-col justify-between gap-8 bg-background p-4 transition-colors duration-200 hover:bg-accent/30 motion-reduce:transition-none sm:p-6">
    <dt className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase group-hover:text-foreground">
      <Link to={to} className={cn("rounded-sm after:absolute after:inset-0", focusRingClassName)}>
        {label}
      </Link>
    </dt>
    <dd className="font-display text-4xl leading-none font-semibold tracking-tighter wrap-break-word tabular-nums sm:text-5xl xl:text-6xl">
      {children}
      {note !== undefined && (
        <span className="mt-2 block font-sans text-xs font-normal tracking-normal text-muted-foreground">
          {note}
        </span>
      )}
    </dd>
  </div>
);

/**
 * What the account holds, as a ruled ledger rather than a row of tiles.
 *
 * The figures are type, not widgets: no icon plates and no card around each, so
 * the numbers are the loudest thing in the band. Duplicates appear only when
 * there are some, since a permanent "0 duplicates" is a warning about nothing.
 */
export const LibraryLedger = ({
  deviceCount,
  deviceLimit,
  duplicateGroupCount,
  projectCount,
  runningTime,
  shelfCount,
  storageLimitBytes,
  storedBytes,
  trackCount,
}: {
  deviceCount: number | undefined;
  deviceLimit: number | null | undefined;
  duplicateGroupCount: number;
  projectCount: number;
  runningTime: { isComplete: boolean; seconds: number };
  shelfCount: number;
  storageLimitBytes: number;
  storedBytes: number;
  trackCount: number;
}) => {
  const { language, t } = useTranslation();
  const albumLengthFormat = useDisplaySettingsStore((state) => state.albumLengthFormat);
  const storedPercent =
    storageLimitBytes > 0 ? Math.min(100, (storedBytes / storageLimitBytes) * 100) : 0;
  const percentLabel = new Intl.NumberFormat(language, { maximumFractionDigits: 0 }).format(
    storedPercent,
  );

  return (
    <section aria-label={t("home.ledger")}>
      <dl
        className={cn(
          "grid grid-cols-2 gap-px border-y-2 border-foreground bg-border sm:grid-cols-3",
          deviceCount === undefined ? "lg:grid-cols-4" : "lg:grid-cols-5",
        )}
      >
        <LedgerFigure label={t("catalog.projects")} to={ROUTES.catalog}>
          {projectCount}
        </LedgerFigure>
        <LedgerFigure label={t("catalog.tracks")} to={ROUTES.catalog}>
          {trackCount}
        </LedgerFigure>
        <LedgerFigure
          label={t("home.runningTime")}
          note={runningTime.isComplete ? undefined : t("home.runningTime.partial")}
          to={ROUTES.catalog}
        >
          {formatAlbumLength({ format: albumLengthFormat, language, seconds: runningTime.seconds })}
        </LedgerFigure>
        <LedgerFigure label={t("navigation.shelves")} to={ROUTES.shelves}>
          {shelfCount}
        </LedgerFigure>
        {deviceCount !== undefined && (
          <LedgerFigure label={t("navigation.devices")} to={ROUTES.devices}>
            {deviceLimit == null
              ? deviceCount
              : t("home.ledger.devices", { total: deviceLimit, used: deviceCount })}
          </LedgerFigure>
        )}
      </dl>

      <div className="flex flex-col gap-3 border-b border-border py-4 sm:flex-row sm:items-center sm:gap-6">
        <span className="shrink-0 text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
          {t("catalog.stored")}
        </span>
        <Progress className="flex-1" value={storedPercent} label={t("catalog.stored")} />
        <span className="shrink-0 text-sm text-muted-foreground tabular-nums">
          {t("catalog.storedOf", {
            limit: formatByteSize(storageLimitBytes, language),
            used: formatByteSize(storedBytes, language),
          })}
          {" · "}
          {t("home.storageUsed", { percent: percentLabel })}
        </span>
        {duplicateGroupCount > 0 && (
          <Link to={ROUTES.catalog} className={cn("shrink-0 rounded-full", focusRingClassName)}>
            <Badge tone="warning">
              <Layers2 className="size-3" aria-hidden />
              {t("catalog.duplicateCount", { count: duplicateGroupCount })}
            </Badge>
          </Link>
        )}
      </div>
    </section>
  );
};
